import { useState, useEffect, useRef } from 'react';

interface UseCountUpOptions {
  end: number;
  duration?: number;
  startOnView?: boolean;
  suffix?: string;
  prefix?: string;
}

export const useCountUp = ({ 
  end, 
  duration = 2000, 
  startOnView = true,
  suffix = '',
  prefix = ''
}: UseCountUpOptions) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    if (!startOnView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [startOnView, hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * end);
      
      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [hasStarted, end, duration]);

  const formattedValue = `${prefix}${count.toLocaleString()}${suffix}`;

  return { ref, count, formattedValue, hasStarted };
};

// Helper to parse stat values like "98%", "2,000+", "9", "50+"
export const parseStatValue = (value: string): { numericValue: number; prefix: string; suffix: string } => {
  const cleanValue = value.replace(/,/g, '');
  const match = cleanValue.match(/^([^\d]*)(\d+)(.*)$/);
  
  if (match) {
    return {
      prefix: match[1] || '',
      numericValue: parseInt(match[2], 10),
      suffix: match[3] || ''
    };
  }
  
  return { prefix: '', numericValue: 0, suffix: '' };
};
